import { startsWith, isUndefined, has } from "lodash";
import Routes from "./routes";
import NavigateActionTypes from "./NavigateActionTypes";
import Constants from "../config/Constants";

import StorageEngine from "../utils/StorageEngine";
import appConfig from "../config/AppConfig";
import DomainUtils from "../utils/DomainUtils";
import DomainMigrationService from "../services/DomainMigrationService";

class NavigationService {
    constructor() {
        this._routes = Routes;

        // build a dictionary for fast processing
        this._layoutRoutesDict = {};
        this._routesDict = {};
        this._routesDictByUID = {};
        for (let i = 0; i < this._routes.length; i++) {
            let layout = this._routes[i].layout;
            if (!this._layoutRoutesDict[layout]) {
                this._layoutRoutesDict[layout] = [];
            }

            this._routesDict[layout + this._routes[i].path] = this._routes[i];
            this._routesDictByUID[this._routes[i].uid] = this._routes[i];
            this._layoutRoutesDict[layout].push(this._routes[i]);
        }
    }

    // fullpath = (layout + path) in the route object
    getRoute(fullpath) {
        return this._routesDict[fullpath];
    }

    getRouteByUID(uid) {
        return this._routesDictByUID[uid];
    }

    getRoutesByUID(uid) {
        let route = this._routesDictByUID[uid];
        return this.getRoutes(route.path);
    }

    getRoutes(layout) {
        let layoutRoutes = this._routes;
        if (layout) {
            layoutRoutes = this._layoutRoutesDict[layout] ? this._layoutRoutesDict[layout] : [];
        }

        return layoutRoutes;
    }

    // getRoutes(layout) {
    //     var layoutRoutes = this._routes;
    //     if (layout) {
    //         layoutRoutes = this._routesDict[layout] ? this._routesDict[layout] : [];
    //     }

    //     return layoutRoutes.map((prop, key) => {
    //         return (
    //             <Route
    //                 path={prop.layout + prop.path}
    //                 component={prop.component}
    //                 key={key}
    //             />
    //         );
    //     });
    // }

    // TODO: need to consider params
    // We will probably append params to the url
    navigateToPage(fromScreen, targetRouteName, params) {
        if (startsWith(targetRouteName, `${Constants.MOBILE_UID_PREFIX}.`)) {
            this.navigateToPageByUID(fromScreen, targetRouteName, params);
        } else {
            let history = fromScreen && fromScreen.props ? fromScreen.props.history : null;
            if (!history) {
                history = fromScreen && fromScreen.props ? fromScreen.props.navigationHistory: null;
            }
            history.push({
                pathname: targetRouteName,
                search: "",
                state: params,
            });
        }
    }

    navigateToPageByUID(fromScreen, targetUID, params) {
        // Get the path name using getPathNameByUID, which already handles networkId logic
        const finalTargetRouteName = this.getPathNameByUID(targetUID);

        // Navigate to the page with the generated path
        this.navigateToPage(fromScreen, finalTargetRouteName, params);
    }

    navigateByAction(fromScreen, actionType, targetRouteName, params) {
        switch (actionType) {
            case NavigateActionTypes.BACK:
                fromScreen.props.history.goBack();
                break;
            // case NavigateActionTypes.RESET: {

            //     break;
            // }
            default:
                // do nothing
                break;
        }
    }

    /**
     * 获取导航参数 - 支持React Router v7
     * 在React Router v7中，需要使用hooks获取路由参数
     * 此方法提供了一个兼容层，可以从hooks或props中获取参数
     * @param {Object} options - 可以是props对象或包含路由hooks结果的对象
     * @returns {Object} 合并后的参数对象
     */
    getNavParams(props) {
        let state, params;
        const search = {};

        if (!isUndefined(props) && has(props, "location")) {
            if (props.location.state) state = props.location.state;
            if (props.location.search) {
                const searchParams = new URLSearchParams(props.location.search.substring(1));
                for (let [k, v] of searchParams.entries()) {
                    search[k] = v;
                }
            }
        }

        if (!isUndefined(props) && has(props, "match")) {
            if (props.match.params) params = props.match.params;
        }

        return { ...state, ...params, ...search };
    }

    getPathNameByUID(targetUID) {
        const route = this.getRouteByUID(targetUID);
        let baseTargetRouteName = route.layout + route.path;

        // Check if this is an account route or other route that doesn't need networkId
        const isAccountRoute = route.layout === "/account" || baseTargetRouteName.startsWith("/account/");
        const isInitialRoute = route.layout === "/initial" || baseTargetRouteName.startsWith("/initial/");

        // For account routes, return the path without networkId
        if (isAccountRoute) {
            return baseTargetRouteName;
        }

        if (isInitialRoute) {
            return baseTargetRouteName;
        }

        // Get school ID using new domain-aware approach
        const currentNetworkId = this.getCurrentNetworkId();

        // Check if we're in subdomain mode
        if (DomainUtils.isSubdomainMode()) {
            // In subdomain mode, never add networkId to path
            // URLs will be like: hust.imleagues.com/admin/dashboard
            return baseTargetRouteName;
        }

        // In path parameter mode (legacy), add networkId to path if available
        if (currentNetworkId && typeof currentNetworkId === "string" && currentNetworkId.length > 0) {
            const cleanNetworkId = currentNetworkId.replace(/^\/+|\/+$/g, "");
            return `/${cleanNetworkId}${baseTargetRouteName}`;
        }

        // If no networkId is available, return the base path
        return baseTargetRouteName;
    }

    /**
     * Get current network ID from multiple sources with priority:
     * 1. Domain (subdomain)
     * 2. Stored object (customDomain for subdomain mode, otherwise shortId)
     * @returns {string|null} Current network ID
     */
    getCurrentNetworkId() {
        // Priority 1: Get from domain
        const domainSchoolId = DomainUtils.getCurrentSchoolId();
        if (domainSchoolId) {
            return domainSchoolId;
        }

        // Priority 2: Get from stored object
        const storedObject = StorageEngine(appConfig.LAST_VISITED_NETWORK_ID_KEY).load();
        if (storedObject) {
            // In subdomain mode, prefer customDomain if available
            if (DomainUtils.isSubdomainMode() && storedObject.customDomain) {
                return storedObject.customDomain;
            }
            // Otherwise use shortId or currentSchoolId
            const storedNetworkId = storedObject.shortId || storedObject.currentSchoolId;
            if (storedNetworkId && typeof storedNetworkId === "string" && storedNetworkId.length > 0) {
                return storedNetworkId;
            }
        }

        return null;
    }

    resetWhenNetworkChanged(screen, networkData, isLoggedIn ) {
        if (screen && screen.props && screen.props.location) {
            const pathName = screen.props.location.pathname;
            const pathParts = pathName.split("/");
            // if is /portal/offerings or /admin/dashboard do not jump

            if (pathParts.length > 1 && pathParts[1] !== "account" && pathParts[1] !== "initial" && pathName.indexOf("/admin/dashboard") === -1 && pathName.indexOf("/portal/offerings") === -1) {
                // Use domain migration service to handle network change
                const handled = DomainMigrationService.handleNetworkChange(networkData);
                
                if (handled) {
                    // Domain migration service handled the navigation (likely subdomain redirect)
                    return;
                }
                
                // Fallback to legacy behavior for path parameter mode
                const networkId = networkData.shortId || networkData.currentSchoolId;
                const newPath = isLoggedIn
                    ? `/${networkId}/admin/dashboard`
                    : `/${networkId}/portal/offerings`;
                
                screen.props.history.push(newPath);
            }
        }
    }
}

export default new NavigationService();
export { NavigationService as NavigationServiceClass };
